import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, formatPrice, Order, Payment, PaymentAction, ServiceStatus } from '../api';
import PaymentForm from '../Components/PaymentForm';
import { Alert, Loading, Spinner, useAction } from '../Components/ui';

interface OrderState {
    order: Order;
    payment: Payment | null;
    service: { id: number; status: ServiceStatus; transfer: boolean } | null;
}

const POLL_MS = 2000;
const FAILED_TEXT: Partial<Record<Payment['status'], string>> = {
    declined: 'The payment was declined.',
    error: 'The payment failed.',
    cancelled: 'The payment was cancelled.',
};

export default function OrderPage() {
    const { orderId } = useParams();
    const [state, setState] = useState<OrderState | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [action, setAction] = useState<PaymentAction | null>(null);
    const cancel = useAction();

    const load = () => api.get<OrderState>(`/orders/${orderId}`)
        .then(res => { setState(res.data); setLoadError(null); })
        .catch(() => setLoadError('Could not load the order.'));

    useEffect(() => { void load(); }, [orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Poll while waiting for Swish or for provisioning to finish.
    const waiting = !!state && (
        state.payment?.status === 'pending' || state.payment?.status === 'created' ||
        // Domain transfers take days; the panel tracks them, so stop polling once one has started.
        (state.order.status === 'paid' && (!state.service || (state.service.status === 'provisioning' && !state.service.transfer)))
    );
    useEffect(() => {
        if (!waiting) return;
        const timer = setInterval(load, POLL_MS);
        return () => clearInterval(timer);
    }, [waiting, orderId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!state) {
        return <section className="section page-section narrow">{loadError ? <Alert type="error">{loadError}</Alert> : <Loading />}</section>;
    }

    const { order, payment, service } = state;
    const paymentOpen = payment && (payment.status === 'pending' || payment.status === 'created');
    const canPay = order.status === 'pending' && !paymentOpen;

    const cancelPayment = () => cancel.run(async () => {
        await api.post(`/orders/${orderId}/cancel-payment`);
        setAction(null);
        await load();
    });

    return (
        <section className="section page-section">
            <span className="section-label">Checkout</span>
            <h1 className="page-title">{order.productName}</h1>
            <p className="section-desc">Order {order.id.slice(0, 8).toUpperCase()} · {order.domain}</p>

            <div className="checkout-grid">
                <div className="form-card" data-aos="fade-up">
                    {canPay && (
                        <>
                            <h3>2. Payment</h3>
                            {payment && FAILED_TEXT[payment.status] && (
                                <Alert type="error">{FAILED_TEXT[payment.status]} {payment.errorMessage ?? ''} You can try again.</Alert>
                            )}
                            <PaymentForm orderId={order.id} onStarted={(p, a) => { setAction(a); setState(s => s && { ...s, payment: p }); }} />
                        </>
                    )}

                    {paymentOpen && (
                        <div className="payment-status">
                            <Spinner />
                            <h3>Waiting for Swish…</h3>
                            <p>
                                {action?.type === 'swish_app'
                                    ? 'Approve the payment in the Swish app. If it did not open, '
                                    : 'Open Swish on your phone and approve the payment.'}
                                {action?.type === 'swish_app' && <a className="text-link" href={action.appUrl}>open Swish</a>}
                            </p>
                            {cancel.error && <Alert type="error">{cancel.error}</Alert>}
                            <button className="btn-outline small" onClick={cancelPayment} disabled={cancel.busy}>Cancel payment</button>
                        </div>
                    )}

                    {order.status === 'paid' && (
                        <div className="payment-status">
                            {service?.transfer && service.status === 'provisioning' ? (
                                <>
                                    <div className="status-icon ok">✓</div>
                                    <h3>Transfer started</h3>
                                    <p>
                                        {order.amountOre > 0 ? 'Payment received and the' : 'The'} transfer of {order.domain} has been submitted. Your current registrar may email the
                                        domain owner to approve it — approving speeds things up. Transfers usually complete within 5 days, and we email you when it's done.
                                    </p>
                                    <Link className="btn-outline" to={`/panel/${service.id}`}>Follow the transfer</Link>
                                </>
                            ) : service?.status === 'active' ? (
                                <>
                                    <div className="status-icon ok">✓</div>
                                    <h3>Your service is ready!</h3>
                                    <p>Payment received and everything is set up.</p>
                                    <Link className="btn-submit inline" to={`/panel/${service.id}`} style={{ textDecoration: 'none' }}>Open control panel →</Link>
                                </>
                            ) : service?.status === 'failed' ? (
                                <>
                                    <div className="status-icon fail">!</div>
                                    <h3>{order.amountOre > 0 ? 'Payment received' : 'Order confirmed'}</h3>
                                    <p>Setting up your service ran into a problem. We have been notified — you can also retry from the control panel.</p>
                                    <Link className="btn-outline" to={`/panel/${service.id}`}>Open control panel</Link>
                                </>
                            ) : (
                                <>
                                    <Spinner />
                                    <h3>{order.amountOre > 0 ? 'Payment received' : 'Order confirmed'}</h3>
                                    <p>Setting up your service on CarbonEdge…</p>
                                </>
                            )}
                        </div>
                    )}

                    {(order.status === 'cancelled' || order.status === 'failed') && (
                        <Alert type="error">This order is {order.status}.</Alert>
                    )}
                </div>

                <div className="form-card" data-aos="fade-up" data-aos-delay="100">
                    <h3>Order summary</h3>
                    <div className="summary-row"><span>Product</span><strong>{order.productName}</strong></div>
                    <div className="summary-row"><span>Domain</span><strong>{order.domain}</strong></div>
                    <div className="summary-row"><span>Status</span><strong>{order.status}</strong></div>
                    <div className="summary-row total"><span>Total</span><strong>{formatPrice(order.amountOre)}</strong></div>
                </div>
            </div>
        </section>
    );
}
